import React, { type ReactNode, type SyntheticEvent } from 'react';
import { createSelector } from 'reselect';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import AsyncAutodev from '@atlassian/jira-autodev/src/async.tsx';
import { AutodevErrorBoundary } from '@atlassian/jira-autodev-common/src/common/ui/error-boundary/index.tsx';
import { AutodevIssueContextStoreContainer } from '@atlassian/jira-autodev/src/controllers/issue-context/index.tsx';
import { useDevOpsDetailsDialogLoaderActions } from '@atlassian/jira-development-details-loader/src/controllers/main.tsx';
import { AppConfigurationPromptSummary } from '@atlassian/jira-development-summary-app-config-prompt-field/src/ui/index.tsx';
import Branch from '@atlassian/jira-development-summary-branch/src/ui/index.tsx';
import Build from '@atlassian/jira-development-summary-build/src/ui/index.tsx';
import Commit from '@atlassian/jira-development-summary-commit/src/ui/index.tsx';
import {
	SUMMARY_ITEM_BRANCH,
	SUMMARY_ITEM_BUILD,
	SUMMARY_ITEM_COMMIT,
	SUMMARY_ITEM_DEPLOYMENT,
	SUMMARY_ITEM_PULLREQUEST,
	SUMMARY_ITEM_REVIEW,
} from '@atlassian/jira-development-summary-common/src/common/constants.tsx';
import { toDetailDialogDataType } from '@atlassian/jira-development-summary-common/src/common/utils.tsx';
import Deployment from '@atlassian/jira-development-summary-deployment-development-field/src/ui/index.tsx';
import PullRequest from '@atlassian/jira-development-summary-pull-request/src/ui/index.tsx';
import Review from '@atlassian/jira-development-summary-review/src/ui/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useAnalyticsEvents,
	type Attributes,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectId, useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import TechnicalPlanner from '@atlassian/jira-technical-planner/src/async.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	Status,
	useIsAutoDevEnabled,
} from '@atlassian/jira-autodev-common/src/services/use-is-autodev-enabled/index.tsx';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import DevelopmentFieldWrapper from '../../common/ui/development-field-wrapper/index.tsx';
import SlideDownOnMount from '../../common/ui/slide-down-on-mount/index.tsx';
import { getAnalyticsIssueData } from '../../controllers/analytics/selectors/index.tsx';
import { issueKeySelector, issueIdSelector } from '../../controllers/context/selectors/index.tsx';
import {
	hasBranchCapabilitiesSelector,
	hasSummaryInformationSelector,
	branchSelector,
	createBranchTargetsSelector,
	summarySectionMessageSelector,
	reviewSummaryItemSelector,
	buildSummaryItemSelector,
	deploymentSummaryItemSelector,
	pullRequestSummaryItemSelector,
	commitSummaryItemSelector,
	legacyDataInformationSelector,
	detailsHasInstanceErrorsSelector,
	hasDevelopmentDataInProjectSelector,
	hasSummaryItemsSelector,
} from '../../controllers/dev-summary/selectors/index.tsx';
import { useHasRovoDevAgentsEntitlement } from '../../services/rovo-dev-agents-entitlements/index.tsx';
import OpenInVSCodeItem from './open-in-vscode/index.tsx';
import ErrorTag from './error-tag/index.tsx';
import OnboardingMessage from './onboarding-message/index.tsx';
import type { StateProps, Props } from './types.tsx';

const NoWrapperComponent = ({ children }: { children: ReactNode }) => <>{children}</>;

const ConditionalSlideDown = componentWithFG(
	'jiv-20508-remove-height-animation-dev-panel',
	NoWrapperComponent,
	SlideDownOnMount,
);

const useIsAutoDevEnabledOnIssueView = (projectKey: string) => {
	const cloudId = useCloudId();
	const atlassianAccountId = useAccountId();
	const [isAutoDevEnabled, state] = useIsAutoDevEnabled(
		atlassianAccountId,
		cloudId,
		getRelayEnvironment(),
		!fg('autodev-disable-rovo-check-in-issue-view'),
	);
	return {
		isEntitlementsLoading: state !== Status.DETERMINED,
		userHasEntitlements: isAutoDevEnabled,
		legacyFeatureflagsEnabled:
			fg('autodev-on-jira-issue') &&
			// eslint-disable-next-line jira/ff/inline-expVal-params
			(expVal<string[]>('autodev-project-enabled', 'value', []).includes(projectKey) ||
				fg('autodev-enabled-by-user')),
	};
};

const useIsCodePlannerEnabledOnIssueView = () => {
	const cloudId = useCloudId();
	const atlassianAccountId = useAccountId();
	const { hasRovoDevAgentsEntitlement } = useHasRovoDevAgentsEntitlement(
		atlassianAccountId,
		cloudId,
		getRelayEnvironment(),
		fg('code-planner-agent-limited-beta') && !fg('devai-code-planner-issue-view-panel'),
	);
	return {
		isCodePlannerEnabledOnIssueView:
			!fg('devai-code-planner-issue-view-panel') &&
			// technical-planner-on-jira-issue is the internal dogfooding flag
			(hasRovoDevAgentsEntitlement || fg('technical-planner-on-jira-issue')),
	};
};

export const Summary = ({
	hasSummaryData,
	showSummarySectionMessage,
	review,
	build,
	deployment,
	pullRequest,
	issueKey,
	issueId,
	hasBranchCapabilities,
	analyticsAttributes,
	branch,
	branchTargets,
	legacyDataSummary,
	commit,
	detailsHasInstanceErrors,
	hasDevelopmentDataInProject,
}: Props) => {
	const { showDevDetailDialog } = useDevOpsDetailsDialogLoaderActions(`devInfoPanelApp${issueKey}`);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const projectKey = useProjectKey(issueKey);
	const projectId = useProjectId(projectKey) ?? '';
	const { userHasEntitlements, legacyFeatureflagsEnabled, isEntitlementsLoading } =
		useIsAutoDevEnabledOnIssueView(projectKey);
	const { isCodePlannerEnabledOnIssueView } = useIsCodePlannerEnabledOnIssueView();

	const onShowDevDetailsDialog = (
		clickEvent?: KeyboardEvent | SyntheticEvent | SyntheticEvent<EventTarget, Event>,
		// @ts-expect-error - TS1016 - A required parameter cannot follow an optional parameter.
		devInfoType: string,
	): void => {
		clickEvent && clickEvent.preventDefault();

		const detailDialogAnalyticsAttributes: Attributes = {
			...analyticsAttributes,
			summaryType: devInfoType,
		};

		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'summaryItem',
			}),
			'devpanelsummary',
			detailDialogAnalyticsAttributes,
		);
		const panelItemType = toDetailDialogDataType(devInfoType);
		if (devInfoType === SUMMARY_ITEM_REVIEW) {
			const summaryItem =
				// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'LegacyDevSummary'.
				legacyDataSummary && legacyDataSummary[panelItemType]
					? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'LegacyDevSummary'.
						legacyDataSummary[panelItemType]
					: undefined;
			showDevDetailDialog(
				// @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'PanelDataTypes'.
				panelItemType,
				summaryItem,
				issueKey,
				issueId,
			);
		} else {
			showDevDetailDialog(
				// @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'PanelDataTypes'.
				panelItemType,
				undefined,
				issueKey,
				issueId,
			);
		}
	};

	const { count: branchesCount = 0 } = branch ?? {};

	return !hasSummaryData &&
		!hasDevelopmentDataInProject &&
		branchTargets.length === 0 &&
		fg('isotopes-devsummary-hide-development-no-connection') ? null : (
		<>
			{!hasSummaryData && showSummarySectionMessage && <OnboardingMessage />}

			<DevelopmentFieldWrapper afterHeadingNode={<ErrorTag />} pinnable>
				<ConditionalSlideDown>
					{isCodePlannerEnabledOnIssueView && (
						<UFOSegment name="view-technical-planner-issue-section">
							<JSErrorBoundary
								packageName="jiraTechnicalPlanner"
								teamName="nova"
								id="JiraTechnicalPlannerDevSummaryField"
								fallback="flag"
							>
								<TechnicalPlanner />
							</JSErrorBoundary>
						</UFOSegment>
					)}
					<>
						<UFOLoadHold name="autodev-enabled-hold" hold={isEntitlementsLoading} />
						{(userHasEntitlements || legacyFeatureflagsEnabled) && (
							<UFOSegment name="view-autodev-issue-section">
								<AutodevErrorBoundary id="JiraAutodevDevSummaryField" fallback="unmount">
									<AutodevIssueContextStoreContainer
										issueId={issueId}
										issueKey={issueKey}
										projectId={projectId}
										projectKey={projectKey}
									>
										<AsyncAutodev issueId={issueId} />
									</AutodevIssueContextStoreContainer>
								</AutodevErrorBoundary>
							</UFOSegment>
						)}
					</>
					{!hasSummaryData && !hasDevelopmentDataInProject && (
						<AppConfigurationPromptSummary
							location="DEVELOPMENT_PANEL"
							panelHasOtherPrompts={hasBranchCapabilities}
						/>
					)}
					{branchTargets.length > 0 &&
						expVal('jira-work-suggestions-open-in-ide-experiment', 'openButton', false) && (
							<OpenInVSCodeItem issueKey={issueKey} />
						)}
					<Branch
						branch={branch}
						branchTargets={branchTargets}
						hasBranchCapabilities={hasBranchCapabilities}
						// @ts-expect-error - TS2322 - Type '(e: any) => void' is not assignable to type '() => void'. | TS7006 - Parameter 'e' implicitly has an 'any' type.
						onClick={(e) => onShowDevDetailsDialog(e, SUMMARY_ITEM_BRANCH)}
						issueId={issueId}
						issueKey={issueKey}
					/>
					<Commit
						commit={commit}
						hasCommitCapabilities={hasBranchCapabilities} // If you have branch capabilities you will also have commit capabilities
						// @ts-expect-error - TS2322 - Type '(e: any) => void' is not assignable to type '() => void'. | TS7006 - Parameter 'e' implicitly has an 'any' type.
						onClick={(e) => onShowDevDetailsDialog(e, SUMMARY_ITEM_COMMIT)}
						issueId={issueId}
						issueKey={issueKey}
					/>
					{hasSummaryData && (
						<PullRequest
							branchesCount={branchesCount}
							hasErrors={detailsHasInstanceErrors}
							pullRequest={pullRequest}
							// @ts-expect-error - TS2322 - Type '(e: any) => void' is not assignable to type '() => void'. | TS7006 - Parameter 'e' implicitly has an 'any' type.
							onClick={(e) => onShowDevDetailsDialog(e, SUMMARY_ITEM_PULLREQUEST)}
							issueId={issueId}
							issueKey={issueKey}
						/>
					)}
					{hasSummaryData && (
						<Review
							review={review}
							// @ts-expect-error - TS2322 - Type '(e: any) => void' is not assignable to type '() => void'. | TS7006 - Parameter 'e' implicitly has an 'any' type.
							onClick={(e) => onShowDevDetailsDialog(e, SUMMARY_ITEM_REVIEW)}
						/>
					)}
					{hasSummaryData && (
						<Build
							build={build}
							// @ts-expect-error - TS2322 - Type '(e: any) => void' is not assignable to type '() => void'. | TS7006 - Parameter 'e' implicitly has an 'any' type.
							onClick={(e) => onShowDevDetailsDialog(e, SUMMARY_ITEM_BUILD)}
						/>
					)}
					{hasSummaryData && (
						<Deployment
							deployment={deployment}
							issueKey={issueKey}
							// @ts-expect-error - TS2322 - Type '(e: any) => void' is not assignable to type '() => void'. | TS7006 - Parameter 'e' implicitly has an 'any' type.
							onClick={(e) => onShowDevDetailsDialog(e, SUMMARY_ITEM_DEPLOYMENT)}
						/>
					)}
				</ConditionalSlideDown>
			</DevelopmentFieldWrapper>
		</>
	);
};

const getAnalyticsIssueDataMemoized = () =>
	createSelector(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(state: any) => getAnalyticsIssueData(state),
		(res) => res,
	);

const mapStateToPropsFactory = () => {
	const getAnalyticsIssueDataInstance = getAnalyticsIssueDataMemoized();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return (state: any): StateProps => ({
		showSummarySectionMessage: summarySectionMessageSelector(state),
		// hasSummaryData includes errors
		hasSummaryData: hasSummaryInformationSelector(state),
		// hasSummaryItems does not include errors
		hasSummaryItems: hasSummaryItemsSelector(state),
		legacyDataSummary: legacyDataInformationSelector(state),
		analyticsAttributes: getAnalyticsIssueDataInstance(state),
		review: reviewSummaryItemSelector(state),
		build: buildSummaryItemSelector(state),
		commit: commitSummaryItemSelector(state),
		deployment: deploymentSummaryItemSelector(state),
		pullRequest: pullRequestSummaryItemSelector(state),
		issueKey: issueKeySelector(state),
		issueId: issueIdSelector(state),
		hasBranchCapabilities: hasBranchCapabilitiesSelector(state),
		branch: branchSelector(state),
		branchTargets: createBranchTargetsSelector(state) ?? [],
		detailsHasInstanceErrors: detailsHasInstanceErrorsSelector(state),
		hasDevelopmentDataInProject: hasDevelopmentDataInProjectSelector(state),
	});
};

export default connect(mapStateToPropsFactory, {})(Summary);
